import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { CheckCircle, Package, Home, ShoppingBag, Clock, Smartphone, Truck, MessageCircle, CreditCard, Wallet } from 'lucide-react';
import { Button, Card } from '../components/common';

const CheckoutSuccessPage: React.FC = () => {
  const location = useLocation();
  // Get order number and payment method from location state or generate new order number
  const locationState = location.state as { orderNumber?: string; paymentMethod?: string } | null;
  const [orderNumber] = useState(locationState?.orderNumber || `ORD-${Date.now().toString().slice(-8)}`);
  const [paymentMethod, setPaymentMethod] = useState<'ecocash' | 'card' | 'cash' | 'paypal'>(
    (locationState?.paymentMethod as 'ecocash' | 'card' | 'cash' | 'paypal') || 'ecocash'
  );
  const [isEcoCashPayment, setIsEcoCashPayment] = useState(false);
  const [isCardPayment, setIsCardPayment] = useState(false);
  const [isPayPalPayment, setIsPayPalPayment] = useState(false);
  const [isCashOnDelivery, setIsCashOnDelivery] = useState(false);
  const [countdown, setCountdown] = useState(300); // 5 minutes for payment verification

  // Extract payment method from location state or URL params
  useEffect(() => {
    // First try to get from location state (passed from checkout page)
    let method = locationState?.paymentMethod as typeof paymentMethod;

    // Fall back to URL params if not in state
    if (!method) {
      const params = new URLSearchParams(location.search);
      method = params.get('payment') as typeof paymentMethod;
    }

    if (method) {
      setPaymentMethod(method);
      setIsEcoCashPayment(method === 'ecocash');
      setIsCardPayment(method === 'card');
      setIsPayPalPayment(method === 'paypal');
      setIsCashOnDelivery(method === 'cash');
    }

    // Start countdown for EcoCash payments only
    if (method === 'ecocash') {
      const timer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [location, locationState]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Get payment method display name
  const getPaymentMethodName = () => {
    switch (paymentMethod) {
      case 'ecocash': return 'EcoCash';
      case 'card': return 'Credit Card';
      case 'paypal': return 'PayPal';
      case 'cash': return 'Cash on Delivery';
      default: return paymentMethod;
    }
  };

  // Get payment method icon
  const getPaymentIcon = () => {
    switch (paymentMethod) {
      case 'ecocash': return <Smartphone className="w-4 h-4" />;
      case 'card': return <CreditCard className="w-4 h-4" />;
      case 'paypal': return <Wallet className="w-4 h-4" />;
      case 'cash': return <Wallet className="w-4 h-4" />;
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-dark-dark dark:bg-white flex items-center justify-center transition-colors duration-300">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Card className="p-8 text-center">
          {/* Success Icon */}
          <div className="w-20 h-20 md:w-24 md:h-24 mx-auto mb-6 bg-accent/20 dark:bg-primary/20 rounded-full flex items-center justify-center">
            <CheckCircle className="w-12 h-12 md:w-16 md:h-16 text-accent dark:text-primary" />
          </div>

          {/* Success Message */}
          <h1 className="text-3xl md:text-4xl font-bold text-white dark:text-gray-900 mb-4 transition-colors duration-300">
            {isEcoCashPayment ? 'Payment Submitted!' : 'Order Confirmed!'}
          </h1>
          
          <p className="text-gray-300 dark:text-gray-700 text-lg mb-6 transition-colors duration-300">
            {isEcoCashPayment 
              ? 'Thank you for your payment! We are verifying your EcoCash transaction.'
              : 'Thank you for your purchase. Your order has been received and is being processed.'}
          </p>

          {/* Order Details */}
          <div className="bg-dark-light dark:bg-gray-50 rounded-lg p-6 mb-8 transition-colors duration-300">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
              <div>
                <p className="text-sm text-gray-400 dark:text-gray-600 mb-1">Order Number</p>
                <p className="font-bold text-white dark:text-gray-900 text-lg">{orderNumber}</p>
              </div>
              <div>
                <p className="text-sm text-gray-400 dark:text-gray-600 mb-1">Payment Method</p>
                <div className="flex items-center justify-center gap-2">
                  <span className="font-bold text-white dark:text-gray-900 capitalize">{getPaymentMethodName()}</span>
                  {getPaymentIcon()}
                </div>
              </div>
              <div>
                <p className="text-sm text-gray-400 dark:text-gray-600 mb-1">Estimated Delivery</p>
                <p className="font-bold text-white dark:text-gray-900">1-2 Business Days</p>
              </div>
            </div>
          </div>

          {/* EcoCash Payment Verification Section - ONLY for EcoCash */}
          {isEcoCashPayment && (
            <div className="mb-8">
              <div className="bg-green-50 dark:bg-gray-800 border border-green-200 dark:border-gray-700 rounded-lg p-6 mb-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-green-100 dark:bg-green-800 rounded-full flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-green-600 dark:text-green-400" />
                  </div>
                  <div className="text-left">
                    <h3 className="text-lg font-semibold text-green-800 dark:text-white mb-2">
                      Payment Verification in Progress
                    </h3>
                    <p className="text-green-700 dark:text-gray-200 mb-4">
                      We're verifying your EcoCash payment. This usually takes 5-15 minutes.
                    </p>
                    
                    {/* Countdown Timer */}
                    <div className="flex items-center gap-4 mb-4">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-green-600 dark:text-green-300 font-mono">
                          {formatTime(countdown)}
                        </div>
                        <p className="text-xs text-green-600 dark:text-gray-300 mt-1">
                          Verification time remaining
                        </p>
                      </div>
                    </div>

                    {/* Verification Steps */}
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <div className="w-6 h-6 rounded-full bg-green-100 dark:bg-green-800 flex items-center justify-center">
                          <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                        </div>
                        <span className="text-green-700 dark:text-gray-200 text-sm">
                          Payment received on EcoCash number
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                          countdown < 150 ? 'bg-green-100 dark:bg-green-800' : 'bg-gray-100 dark:bg-gray-700'
                        }`}>
                          {countdown < 150 ? (
                            <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                          ) : (
                            <Clock className="w-4 h-4 text-gray-500 dark:text-gray-500" />
                          )}
                        </div>
                        <span className={`text-sm ${
                          countdown < 150 
                            ? 'text-green-700 dark:text-gray-200' 
                            : 'text-gray-700 dark:text-gray-400'
                        }`}>
                          Payment matched to order
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                          countdown === 0 ? 'bg-green-100 dark:bg-green-800' : 'bg-gray-100 dark:bg-gray-700'
                        }`}>
                          {countdown === 0 ? (
                            <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                          ) : (
                            <Clock className="w-4 h-4 text-gray-500 dark:text-gray-500" />
                          )}
                        </div>
                        <span className={`text-sm ${
                          countdown === 0 
                            ? 'text-green-700 dark:text-gray-200' 
                            : 'text-gray-700 dark:text-gray-400'
                        }`}>
                          Order confirmed and processing
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Important Notes for EcoCash */}
              <div className="bg-yellow-50 dark:bg-gray-800 border border-yellow-200 dark:border-gray-700 rounded-lg p-4">
                <h4 className="font-semibold text-yellow-800 dark:text-yellow-300 mb-2">
                  📱 Keep your phone handy
                </h4>
                <p className="text-yellow-700 dark:text-yellow-400 text-sm">
                  We'll send SMS updates about your payment verification and delivery status.
                  Make sure to save our number: <strong className="text-yellow-900 dark:text-yellow-300">+263 77 352 0100</strong>
                </p>
              </div>
            </div>
          )}

          {/* Card Payment Success - ONLY for Card */}
          {isCardPayment && (
            <div className="mb-8">
              <div className="bg-green-50 dark:bg-gray-800 border border-green-200 dark:border-gray-700 rounded-lg p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-green-100 dark:bg-green-800 rounded-full flex items-center justify-center flex-shrink-0">
                    <CreditCard className="w-6 h-6 text-green-600 dark:text-green-400" />
                  </div>
                  <div className="text-left">
                    <h3 className="text-lg font-semibold text-green-800 dark:text-white mb-2">
                      Payment Processed Successfully
                    </h3>
                    <p className="text-green-700 dark:text-gray-200">
                      Your card payment has been approved. A confirmation email has been sent to your registered email address.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* PayPal Payment Success - ONLY for PayPal */}
          {isPayPalPayment && (
            <div className="mb-8">
              <div className="bg-blue-50 dark:bg-gray-800 border border-blue-200 dark:border-gray-700 rounded-lg p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-100 dark:bg-blue-800 rounded-full flex items-center justify-center flex-shrink-0">
                    <Wallet className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="text-left">
                    <h3 className="text-lg font-semibold text-blue-800 dark:text-white mb-2">
                      PayPal Payment Complete
                    </h3>
                    <p className="text-blue-700 dark:text-gray-200">
                      Your PayPal payment has been processed. You'll receive an email confirmation from PayPal and from us shortly.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Cash on Delivery Info - ONLY for Cash */}
          {isCashOnDelivery && (
            <div className="mb-8">
              <div className="bg-purple-50 dark:bg-gray-800 border border-purple-200 dark:border-gray-700 rounded-lg p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-purple-100 dark:bg-purple-800 rounded-full flex items-center justify-center flex-shrink-0">
                    <Wallet className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div className="text-left">
                    <h3 className="text-lg font-semibold text-purple-800 dark:text-white mb-2">
                      Payment on Delivery
                    </h3>
                    <p className="text-purple-700 dark:text-gray-200">
                      No payment needed now! Simply pay when your order arrives. Our delivery driver will collect the payment.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Next Steps - Different for each payment method */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-white dark:text-gray-900 mb-4 transition-colors duration-300">
              {isEcoCashPayment ? 'Next Steps After Verification' : 'What happens next?'}
            </h3>
            <div className="space-y-4">
              {/* Step 1: Changes based on payment method */}
              <div className="flex items-center gap-4 p-4 bg-dark-light dark:bg-gray-50 rounded-lg transition-colors duration-300">
                {isEcoCashPayment ? (
                  <Smartphone className="w-6 h-6 text-accent dark:text-primary flex-shrink-0" />
                ) : isCardPayment ? (
                  <CreditCard className="w-6 h-6 text-accent dark:text-primary flex-shrink-0" />
                ) : (
                  <Package className="w-6 h-6 text-accent dark:text-primary flex-shrink-0" />
                )}
                <div className="text-left">
                  <p className="font-medium text-white dark:text-gray-900 transition-colors duration-300">
                    {isEcoCashPayment ? 'Payment Verification' : 
                     isCardPayment ? 'Payment Confirmed' : 
                     isPayPalPayment ? 'Payment Processing' : 'Order Received'}
                  </p>
                  <p className="text-sm text-gray-400 dark:text-gray-600 transition-colors duration-300">
                    {isEcoCashPayment 
                      ? 'We\'ll confirm your EcoCash payment and update you via SMS'
                      : isCardPayment
                      ? 'Your payment has been processed and confirmed'
                      : isPayPalPayment
                      ? 'PayPal has received your payment'
                      : 'We\'ve received your order and will prepare it shortly'}
                  </p>
                </div>
              </div>
              
              {/* Step 2: Order Preparation */}
              <div className="flex items-center gap-4 p-4 bg-dark-light dark:bg-gray-50 rounded-lg transition-colors duration-300">
                <Package className="w-6 h-6 text-accent dark:text-primary flex-shrink-0" />
                <div className="text-left">
                  <p className="font-medium text-white dark:text-gray-900 transition-colors duration-300">
                    Order Preparation
                  </p>
                  <p className="text-sm text-gray-400 dark:text-gray-600 transition-colors duration-300">
                    Our team picks and packs the freshest items for you
                  </p>
                </div>
              </div>
              
              {/* Step 3: Delivery */}
              <div className="flex items-center gap-4 p-4 bg-dark-light dark:bg-gray-50 rounded-lg transition-colors duration-300">
                <Truck className="w-6 h-6 text-accent dark:text-primary flex-shrink-0" />
                <div className="text-left">
                  <p className="font-medium text-white dark:text-gray-900 transition-colors duration-300">
                    Delivery Dispatch
                  </p>
                  <p className="text-sm text-gray-400 dark:text-gray-600 transition-colors duration-300">
                    {isCashOnDelivery 
                      ? 'You\'ll receive SMS tracking updates. Pay our driver upon delivery.'
                      : 'You\'ll receive SMS tracking updates when your order ships'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              variant="primary"
              size="lg"
              as={Link}
              to="/orders"
              leftIcon={<Package className="w-5 h-5" />}
            >
              {isEcoCashPayment ? 'Track Verification' : 'View Order'}
            </Button>
            <Button
              variant="outline"
              size="lg"
              as={Link}
              to="/products"
              leftIcon={<ShoppingBag className="w-5 h-5" />}
              className="hover:text-white dark:hover:text-white"
            >
              Continue Shopping
            </Button>
            <Button
              variant="outline"
              size="lg"
              as={Link}
              to="/"
              leftIcon={<Home className="w-5 h-5" />}
              className="hover:text-white dark:hover:text-white"
            >
              Back to Home
            </Button>
          </div>

          {/* Support Information */}
          <div className="mt-8 pt-6 border-t border-dark-light dark:border-gray-200">
            <h4 className="font-semibold text-white dark:text-gray-900 mb-3 transition-colors duration-300">
              Need help with your order?
            </h4>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a
                href="https://wa.me/+263773520100"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors duration-300"
              >
                <MessageCircle className="w-4 h-4" />
                WhatsApp Support
              </a>
              <a
                href="tel:+263773520100"
                className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors duration-300"
              >
                <Smartphone className="w-4 h-4" />
                Call: +263 77 352 0100
              </a>
              <a
                href="mailto:support@angelcite.com"
                className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-accent hover:bg-accent-dark text-white rounded-lg transition-colors duration-300"
              >
                ✉️ Email Support
              </a>
            </div>
            <p className="text-sm text-gray-400 dark:text-gray-600 mt-4 transition-colors duration-300">
              Order Reference: <code className="bg-dark-light dark:bg-gray-100 px-2 py-1 rounded">{orderNumber}</code>
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default CheckoutSuccessPage;

